import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { TiersPlafond, PlafondCheckResult } from '../models/models';
import api from './api';

interface TiersPlafondState {
  /** Plafonds chargés pour le tier sélectionné (global + per-dept). */
  plafonds: TiersPlafond[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  /** Dernier résultat de check (optionnel — preview UI). */
  lastCheck: PlafondCheckResult | null;
}

const initialState: TiersPlafondState = {
  plafonds: [],
  loading: false,
  error: null,
  successMessage: null,
  lastCheck: null,
};

// ── Thunks ───────────────────────────────────────────────────────────────────

/** Charge tous les plafonds (global + per-dept) d'un tier. */
export const fetchPlafondsByTiers = createAsyncThunk<
  TiersPlafond[],
  { tiers_id: number },
  { rejectValue: string }
>(
  'tiersPlafond/fetchByTiers',
  async ({ tiers_id }, thunkAPI) => {
    try {
      const response = await api.get('/tiersPlafond/byTiers', { params: { tiers_id } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec du chargement des plafonds.'
      );
    }
  }
);

/** Upsert un plafond (id=-1 pour création — la SP gère l'upsert par tier+dept). */
export const createOrUpdatePlafond = createAsyncThunk<
  TiersPlafond,
  TiersPlafond,
  { rejectValue: string }
>(
  'tiersPlafond/createOrUpdate',
  async (plafond, thunkAPI) => {
    try {
      const response = await api.post('/tiersPlafond', plafond);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec de la sauvegarde.'
      );
    }
  }
);

export const deletePlafond = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'tiersPlafond/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/tiersPlafond', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec de la suppression.'
      );
    }
  }
);

/**
 * Lecture des données brutes (balances + plafonds) pour preview UI.
 * Le calcul du dépassement éventuel est fait côté composant à partir des
 * 4 chiffres retournés. L'enforcement transactionnel se fait au backend.
 */
export const checkPlafond = createAsyncThunk<
  PlafondCheckResult,
  { tiers_id: number; departement_id: number },
  { rejectValue: string }
>(
  'tiersPlafond/check',
  async (params, thunkAPI) => {
    try {
      const response = await api.get('/tiersPlafond/check', { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec de la vérification.'
      );
    }
  }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const tiersPlafondSlice = createSlice({
  name: 'tiersPlafond',
  initialState,
  reducers: {
    clearPlafonds: (state) => {
      state.plafonds  = [];
      state.lastCheck = null;
    },
    clearMessages: (state) => {
      state.error          = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlafondsByTiers.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchPlafondsByTiers.fulfilled, (state, action: PayloadAction<TiersPlafond[]>) => {
        state.loading  = false;
        state.plafonds = action.payload;
      })
      .addCase(fetchPlafondsByTiers.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || null;
      })

      .addCase(createOrUpdatePlafond.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(createOrUpdatePlafond.fulfilled, (state, action: PayloadAction<TiersPlafond>) => {
        state.loading        = false;
        state.successMessage = 'Plafond enregistré';
        // Upsert local : remplace si même id, sinon push
        const idx = state.plafonds.findIndex(p => p.id === action.payload.id);
        if (idx >= 0) state.plafonds[idx] = action.payload;
        else          state.plafonds.push(action.payload);
      })
      .addCase(createOrUpdatePlafond.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || null;
      })

      .addCase(deletePlafond.fulfilled, (state, action: PayloadAction<number>) => {
        state.successMessage = 'Plafond supprimé';
        state.plafonds       = state.plafonds.filter(p => p.id !== action.payload);
      })
      .addCase(deletePlafond.rejected, (state, action) => {
        state.error = action.payload || null;
      })

      .addCase(checkPlafond.fulfilled, (state, action: PayloadAction<PlafondCheckResult>) => {
        state.lastCheck = action.payload;
      });
  },
});

export const { clearPlafonds, clearMessages } = tiersPlafondSlice.actions;
export default tiersPlafondSlice.reducer;
